import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const categoryIcons = [
    'ri-shopping-bag-line', 'ri-star-line', 'ri-t-shirt-line', 'ri-shield-check-line',
    'ri-links-line', 'ri-user-line', 'ri-book-open-line', 'ri-shield-check-line',
    'ri-user-line', 'ri-vip-crown-line', 'ri-shopping-bag-line', 'ri-links-line',
    'ri-drop-line', 'ri-question-line',
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/90 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
                Kakobuy<span className="text-primary-500">Finds</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-foreground-800 hover:bg-background-200 transition-colors duration-200 cursor-pointer text-sm font-medium whitespace-nowrap"
                >
                  <span>Kategorie</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-[640px] bg-background-50 rounded-2xl border border-background-200/70 p-6 grid grid-cols-3 gap-3 z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer text-sm font-semibold whitespace-nowrap"
              >
                Kakobuy Sheet
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-900 text-white hover:bg-foreground-800 transition-colors duration-200 cursor-pointer text-sm font-semibold whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line"></i>
                <span>Buy It Now</span>
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-foreground-900/40 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed top-16 left-0 right-0 bottom-0 z-50 bg-background-50 overflow-y-auto md:hidden">
            <div className="px-5 py-6">
              <div className="mb-2">
                <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-[0.12em] mb-3">
                  Przeglądaj kategorie
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat, i) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-background-100 border border-background-200/60 hover:bg-primary-50 hover:border-primary-200 transition-all duration-150 cursor-pointer"
                    >
                      <span className="w-7 h-7 rounded-lg bg-background-50 border border-background-200/60 flex items-center justify-center flex-shrink-0">
                        <i className={`${categoryIcons[i]} text-primary-500 text-xs`}></i>
                      </span>
                      <span className="text-sm text-foreground-800 font-medium leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-background-200/60">
                <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-[0.12em] mb-3">
                  Szybkie akcje
                </p>
                <div className="flex flex-col gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer text-sm font-semibold whitespace-nowrap"
                  >
                    <i className="ri-external-link-line"></i>
                    <span>Kakobuy Sheet</span>
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-foreground-900 text-white hover:bg-foreground-800 transition-colors duration-200 cursor-pointer text-sm font-semibold whitespace-nowrap"
                  >
                    <i className="ri-shopping-bag-line"></i>
                    <span>Buy It Now</span>
                  </a>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-background-200/60">
                <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-[0.12em] mb-3">
                  Artykuły
                </p>
                <div className="flex flex-col gap-1">
                  <Link
                    to="/artykul/kakobuy-sheet-kompletny-przewodnik"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                  >
                    <i className="ri-book-open-line text-accent-500 text-sm"></i>
                    <span className="text-sm text-foreground-700">Kompletny przewodnik</span>
                  </Link>
                  <Link
                    to="/artykul/najlepsze-produkty-kakobuy"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                  >
                    <i className="ri-star-line text-accent-500 text-sm"></i>
                    <span className="text-sm text-foreground-700">Najlepsze produkty 2026</span>
                  </Link>
                  <Link
                    to="/artykul/porownanie-spreadsheet-kakobuy-cssbuy"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                  >
                    <i className="ri-scales-3-line text-accent-500 text-sm"></i>
                    <span className="text-sm text-foreground-700">Porównanie platform</span>
                  </Link>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-background-200/60 pb-4">
                <a
                  href="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                >
                  <i className="ri-home-line text-primary-500 text-sm"></i>
                  <span className="text-sm text-foreground-700 font-medium">Strona główna</span>
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}